import React, { useState } from 'react';
import { connect } from 'react-redux';
import produce from 'immer';
import { cloneDeep } from '../../../utils/lodash';

import { withStyles } from '@mui/styles';

import { ButtonBase } from '@mui/material';

import { Add, Edit, CopyAllOutlined, RemoveOutlined, DragIndicator } from '@mui/icons-material';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';

import { sortableHandle, SortableElement, SortableContainer } from 'react-sortable-hoc';

import Node from '../../../common/Node';
import CommonStyles from '../../../common/CommonStyles';
import ArrowTooltipCss from '../../../common/ArrowTooltipCss';
import TrplFormEdit from './TrplFormEdit/TrplFormEdit';
import ComponentWrapper from '../../../common/ComponentWrapper';
import FullScreenDialog from '../../../common/FullScreenDialog';
import TrplSelect from '../../../common/TrplControls/TrplSelect';
import ViewAccessRulesList from '../../../common/ViewAccessRulesList';

import { changeArrayElementPosition, getNodesIdsFromGraphTrigger } from '../../../utils';

import { updateJels } from '../../../reducer/jelsReducer';
import { TRPL_SELECT_PLACEHOLDER } from '../../../constants';

const styles = {
  accountGroupTitle: {
    fontSize: 14,
    color: '#878787',
    fontWeight: 600,
    marginBottom: 10,
    textTransform: 'uppercase',
  },
  frm: {
    minHeight: 34,
    display: 'flex',
    marginBottom: 1,
    background: '#fff',
    alignItems: 'center',
    justifyContent: 'space-between',
    '&:hover': {
      background: '#F7F7F7',
    },
  },
  draggableContainer: {
    minHeight: 34,
    display: 'flex',
    background: '#F7F7F7',
    flexDirection: 'column',
    // alignItems: 'center',
    // justifyContent: 'space-between',
    '&:hover': {
      //background: '#F7F7F7',
    },
  },
  frmBtn: {
    fontSize: 20,
    color: '#C4C4C4',
    cursor: 'pointer',
    '&:hover': {
      color: '#878787',
    },
  },
  frmBtnContainer: {
    marginRight: 30,
  },
  frmDeleteBtn: {
    '&:hover': {
      color: '#E91818',
      cursor: 'pointer',
    },
  },
  boundValueDragIconContainer: {
    width: '30px',
    height: '100%',
    display: 'flex',
    flexShrink: '0',
    marginRight: '10px',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      '& .drag-handle-icon': {
        color: '#4E4C4C',
      },
    },
  },
  boundValueDragIcon: {
    color: '#C4C4C4',
    fontSize: '15px',
  },
  addNewForm: {
    padding: 10,
    marginBottom: 20,
    background: '#fff',
  },
  saveDashboardBtn: {
    '&:hover': {
      '& .save-btn-loader div': {
        backgroundColor: '#fff',
      },
    },
  },
  ...CommonStyles,
};

const DragHandle = sortableHandle(({ classes }) => (
  <div className={classes.boundValueDragIconContainer}>
    <DragIndicator className={`drag-handle-icon ${classes.boundValueDragIcon}`} />
  </div>
));

const FormDraggable = SortableElement(({
  frm,
  concept,
  classes,
  copyFrm,
  frmViews,
  itemIndex,
  removeJel,
  accessRules,
  accessRulesNodes,
  updateRuleOnSave,
  deleteAccessRule,
  addNodeToNodesData,
  openTrplFormEditModal,
  addRuleToAccessRulesArray,
}) => {
  const [accessRulesOpened, setAccessRulesOpened] = useState(false);

  const frmAccessRules = accessRules.length ? accessRules.filter(rule =>
    rule.categories && rule.categories.includes(concept.id)
    && frmViews.includes(rule.views[0].id)) : [];

  return (
    <React.Fragment>
    <li className={`${classes.draggableContainer}`}>
      <div className={`${classes.frm}`}>
      <div className={classes.alignCenter}>
        <div className={classes.boundValueContainer}>
          <DragHandle classes={classes} />
        </div>

        <div
          style={{
            color: `${!frm ? 'red' : ''}`,
            fontWeight: 600,
            cursor: 'pointer',
          }}
          onClick={() => openTrplFormEditModal(false, frm)}
        >
          {frm ? `${frm.id} ${frm.title}` : "Form with this id doesn't exist"}
        </div>
      </div>

      <div className={classes.alignCenter}>
        <div
          className={classes.alignCenter}
          onClick={() => setAccessRulesOpened(!accessRulesOpened)}
          style={{ marginRight: '50px', cursor: 'pointer' }}
        >
          <span>Access rules </span>
          <div
            style={{
              height: '24px',
              transform: `${accessRulesOpened ? '' : 'rotate(90deg)'}`,
            }}
          >
            <KeyboardArrowDownOutlinedIcon/>
          </div>
        </div>

        <div className={classes.frmBtnContainer}>
          <ArrowTooltipCss text="Edit">
            <Edit onClick={() => openTrplFormEditModal(false, frm)} className={`frm-btn ${classes.frmBtn}`} />
          </ArrowTooltipCss>
        </div>

        <div className={classes.frmBtnContainer}>
          <ArrowTooltipCss text="Copy">
            <CopyAllOutlined
              onClick={() => {
                copyFrm(frm);
              }}
              className={`frm-btn ${classes.frmBtn}`}
            />
          </ArrowTooltipCss>
        </div>

        <div className={classes.frmBtnContainer}>
          <ArrowTooltipCss text="Remove">
            <RemoveOutlined
              style={{ fontSize: 24 }}
              className={`frm-btn ${classes.frmBtn}`}
              onClick={() => removeJel(itemIndex)}
            />
          </ArrowTooltipCss>
        </div>
      </div>
      </div>
      
      {accessRulesOpened &&
        <ViewAccessRulesList
          concept={concept}
          viewsIds={frmViews}
          accessRules={frmAccessRules}
          allAccessRules={accessRules}
          accessRulesNodes={accessRulesNodes}
          updateRuleOnSave={updateRuleOnSave}
          deleteAccessRule={deleteAccessRule}
          addNodeToNodesData={addNodeToNodesData}
          addRuleToAccessRulesArray={addRuleToAccessRulesArray}
        />
      }
    </li>
    </React.Fragment>
  );
});

const FormsContainer = SortableContainer(({
  items,
  copyFrm,
  removeJel,
  jels,
  openTrplFormEditModal,
  classes,
  concept,
  accessRules,
  accessRulesNodes,
  nestedDashboardViews,
  deleteAccessRule,
  updateRuleOnSave,
  addNodeToNodesData,
  addRuleToAccessRulesArray,
}) => {
  return (
    <div>
      {items.map((item, itemIndex) => {
        let frm = null;

        if (item.type === 'FORM') frm = jels.jelData[item.value];

        return frm ? (
          <FormDraggable
            frm={frm}
            jels={jels}
            key={frm.id}
            concept={concept}
            index={itemIndex}
            classes={classes}
            copyFrm={copyFrm}
            itemIndex={itemIndex}
            removeJel={removeJel}
            accessRules={accessRules}
            accessRulesNodes={accessRulesNodes}
            updateRuleOnSave={updateRuleOnSave}
            deleteAccessRule={deleteAccessRule}
            addNodeToNodesData={addNodeToNodesData}
            openTrplFormEditModal={openTrplFormEditModal}
            addRuleToAccessRulesArray={addRuleToAccessRulesArray}
            frmViews={(nestedDashboardViews && nestedDashboardViews[frm.id]) || []}
          />
        ) : null;
      })}
    </div>
  );
});

class DashboardEdit extends ComponentWrapper {
  state = {
    concept: {
      id: 0,
      title: '',
    },
    dashboard: {
      views: [],
      acc_concept: 0,
      enabled: true,
    },
    editFrmModal: {
      data: {},
      isOpened: false,
    },
    deleteJelModal: {
      open: false,
    },
    accessRules: [],
    accessRulesNodes: [],
    nestedDashboardViews: {},
    showSavingLoader: false,
  };

  componentDidMount() {
    const { dashboard } = this.state;
    const { concept } = this.props;

    const data = { concept, dashboard };

    if (this.props.dashboard) {
      data.dashboard = this.props.dashboard;
    } else if (concept) {
      data.dashboard.acc_concept = +concept.id;
    }

    this.setState(data);

    if (data.dashboard.id) this.getDashboardFormViews(data.dashboard.id);

  };

  collectNodeIdsFromGraphTriggers = (accessRules) => {
    const { setup_rbox_light } = this.props.light_meta_and_ops;
    const { setup } = this.props;

    const nodesIds = getNodesIdsFromGraphTrigger(accessRules, setup_rbox_light);

    const data = {
      api_key: setup.id,
      nesting: 2,
      filter: {
        nodes: nodesIds,
        page: 1,
        limit: 100,
      },
      sorter: {
        sort_pid: '',
        sort_by: '',
      },
    };

    this.postWithData({
      url: `/pnkqry3`,
      data,
      cb: (data) => {
        if (data.nodes) this.setState({ accessRulesNodes: data.nodes });
      }
    });
  };

  addNodeToNodesData = (node) => {
    this.setState({ accessRulesNodes: { ...this.state.accessRulesNodes, [node.id]: node } })
  };

  getDashboardFormViews = (dashboardId) => {
    this.get({
      url: `/rest/get_dashboards_views`,
      cb: (response) => {
        if (response._status === 'ok') {
          const nestedDashboardViews = response.dashboards_views[dashboardId];

          this.getAllAccessRules(nestedDashboardViews);
        }
      }
    })
  };

  deleteAccessRule = (ruleToDelete) => {
    const nextImmutableAccessRules = produce(this.state.accessRules, draftAccessRules => {
      const indentificator = ruleToDelete.temporary_id ? 'temporary_id' : 'id';
      const ruleIndex = this.state.accessRules.findIndex(rule => rule[indentificator] === ruleToDelete[indentificator]);
      draftAccessRules.splice(ruleIndex, 1);
    });

    if (ruleToDelete.id) {
      this.postWithData({
        url: `/rest/del_access_rule`,
        data: {
          id: ruleToDelete.id,
        },
        cb: (res) => {
          if (res._status === 'ok') {
            this.setState({ accessRules: [...nextImmutableAccessRules]});
          }
        },
      });
    } else {
      this.setState({ accessRules: [...nextImmutableAccessRules]});
    }
  };

  getAllAccessRules = (nestedDashboardViews) => {
    this.get({
      url: `/rest/get_access_rules`,
      cb: (res) => {
        if (res.access_rules) {
          this.setState({
            accessRules: res.access_rules.reverse(),
            nestedDashboardViews
          })
        }
        if (res.access_rules.length && !this.state.accessRulesNodes.length) this.collectNodeIdsFromGraphTriggers(res.access_rules);
      }
    });
  };

  updateRuleOnSave = (updatedRule) => {
    const immutableAccessRules = produce(this.state.accessRules, draftAccessRules => {
      const indentificator = updatedRule.temporary_id ? 'temporary_id' : 'id';
      const ruleIndex = this.state.accessRules.findIndex(rule => rule[indentificator] === updatedRule[indentificator]);
      draftAccessRules.splice(ruleIndex, 1, {...updatedRule});
    });

    this.setState({ accessRules: [...immutableAccessRules] });
  };

  addRuleToAccessRulesArray = (newAccessRule) => {
    const immutableAccessRules = produce(this.state.accessRules, draftRules => {
      draftRules.unshift(newAccessRule);
    });

    this.setState({ accessRules: [...immutableAccessRules] });
  };

  onSortEnd = ({ oldIndex, newIndex }) => {
    const dashboard = { ...this.state.dashboard };
    const dashboardViews = [...dashboard.views];

    dashboard.views = changeArrayElementPosition(dashboardViews, oldIndex, newIndex);

    this.setState({ dashboard });
  };

  removeJel = (index) => {
    const dashboard = { ...this.state.dashboard };
    const dashboardViews = [...dashboard.views];

    dashboardViews.splice(index, 1);
    dashboard.views = dashboardViews;

    this.setState({ dashboard }, () => {
      this.onSaveDashboardClick();
    });
  };

  openTrplFormEditModal = (isNewFrm, data) => {
    let editFrmModal = { ...this.state.editFrmModal };

    const newForm = {
      createMode: true,
      is_dashboard: true,
      concept: null,
      title: '',
      groups: [],
    };

    if (isNewFrm) {
      editFrmModal = {
        isOpened: true,
        data: {
          ...newForm,
        },
      };
    } else {
      editFrmModal.data = data;
      editFrmModal.isOpened = true;
    }

    this.setState({ editFrmModal });
  };

  addFrmToDashboard = (value) => {
    const dashboard = { ...this.state.dashboard };
    const dashboardViews = [...dashboard.views];

    let isAlreadyInDashboard = false;

    dashboardViews.forEach((view) => {
      if (view.value === value) isAlreadyInDashboard = true;
    });

    if (!isAlreadyInDashboard)
      dashboardViews.push({
        type: 'FORM',
        value,
      });

    dashboard.views = dashboardViews;

    this.setState({ dashboard }, () => {
      this.onSaveDashboardClick();
    });
  };

  onSaveDashboardClick = () => {
    this.setState({ showSavingLoader: true }, () => {
      const { dashboard } = this.state;

      this.postWithData({
        url: '/rest/upd_jel?type=dashboard',
        data: dashboard,
        cb: (data) => {
          const dashboard = data.dashboard;

          this.setState({ dashboard, showSavingLoader: false }, () => this.getDashboardFormViews(dashboard.id));
        },
      });
    });
  };

  updateParentModalOnSave  = (jel) => {
    const { editFrmModal } = this.state;
    const modalDataCopy = cloneDeep(editFrmModal);

    modalDataCopy.data = cloneDeep(jel);

    this.setState({ editFrmModal: modalDataCopy });
  };

  copyFrm = (frm) => {
    const { copyFrmInDashboard } = this.props;

    const dashboard = { ...this.state.dashboard };
    const dashboardViews = [...dashboard.views];

    copyFrmInDashboard(frm, (copiedFrm) => {
      dashboardViews.push({
        type: 'FORM',
        value: copiedFrm.id,
      });

      dashboard.views = dashboardViews;

      this.setState({ dashboard });
    });
  };

  render() {
    const { classes, jels, onCopyViewBtnClick, deepCopyView, account, modalIndex } = this.props;
    const { concept, dashboard, editFrmModal, nestedDashboardViews, accessRules, accessRulesNodes } = this.state;

    const addNewFormValues = [
      {
        disabled: true,
        title: 'Add form',
        value: TRPL_SELECT_PLACEHOLDER,
        style: {
          fontStyle: 'italic',
        },
      },
    ];

    const dashboardViewsIds = [];
    dashboard && dashboard.views && dashboard.views.forEach((view) => dashboardViewsIds.push(view.value));

    jels.forms.forEach((frmId) => {
      const frm = jels.jelData[frmId];

      if (frm.is_dashboard && !dashboardViewsIds.includes(frm.id))
        addNewFormValues.push({ title: `${frm.id} ${frm.title}`, value: frm.id });
    });

    const currentModalIndex = modalIndex ? modalIndex : 0;

    return (
      <div className={classes.mb50}>
        <FullScreenDialog
          modalIndex={currentModalIndex}
          isOpen={editFrmModal.isOpened}
          disableContainerPadding={true}
          title={
            editFrmModal.data.id && !isNaN(editFrmModal.data.id) ? `Form edit - ${editFrmModal.data.title} | ${editFrmModal.data.id}` : 'Form create'
          }
          onClose={() => {
            this.setState({
              editFrmModal: {
                isOpened: false,
                data: {},
              },
            });

            this.props.updateJels();
          }}
        >
          <TrplFormEdit
            modalIndex={currentModalIndex + 1}
            setParentJel={(createdFrmId) => {
              if (!editFrmModal.data.id) this.addFrmToDashboard(createdFrmId);
            }}
            form={editFrmModal.data}
            dashboard={editFrmModal.data}
            deepCopyView={deepCopyView}
            onCopyViewBtnClick={onCopyViewBtnClick}
            updateParentModalOnSave={this.updateParentModalOnSave}
          />
        </FullScreenDialog>

        <div className={classes.mb20}>
          <div className={classes.accountGroupTitle}>Account group</div>

          {concept && concept.id ? <Node isLink nodeId={concept.id} size="md" node={concept} /> : null}
        </div>

        {dashboard && dashboard.views ? (
          <FormsContainer
            jels={jels}
            useDragHandle
            classes={classes}
            concept={concept}
            copyFrm={this.copyFrm}
            items={dashboard.views}
            accessRules={accessRules}
            removeJel={this.removeJel}
            onSortEnd={this.onSortEnd}
            accessRulesNodes={accessRulesNodes}
            updateRuleOnSave={this.updateRuleOnSave}
            deleteAccessRule={this.deleteAccessRule}
            helperClass={classes.draggableItemHelper}
            nestedDashboardViews={nestedDashboardViews}
            addNodeToNodesData={this.addNodeToNodesData}
            openTrplFormEditModal={this.openTrplFormEditModal}
            addRuleToAccessRulesArray={this.addRuleToAccessRulesArray}
          />
        ) : null}

        <div className={classes.addNewForm} style={{ pointerEvents: account && account.access_level > 1 ? '' : 'none' }}>
          <TrplSelect
            margin="none"
            value={TRPL_SELECT_PLACEHOLDER}
            style={{ width: 400 }}
            complicatedValues
            values={addNewFormValues}
            onChange={(e) => {
              if (e.target.value) this.addFrmToDashboard(e.target.value);
            }}
            firstChilds={
              <ButtonBase
                style={{ textTransform: 'none' }}
                onClick={() => {
                  this.openTrplFormEditModal(true);
                }}
                className={`${classes.btnGray} ${classes.mb5}`}
              >
                Create form <Add className={`btn-gray-icon ${classes.btnGrayIcon}`} />
              </ButtonBase>
            }
          />
        </div>
      </div>
    );
  }
}

export default connect((state) => ({
  jels: state.jels,
  setup: state.meta.setup,
  account: state.meta.account,
  light_meta_and_ops: state.meta.light_meta_and_ops
}), { updateJels })(withStyles(styles)(DashboardEdit));